import {useCallback, useEffect, useState} from "react";

type ReturnValue = {
    /**
     * The offset of the drag in pixels
     */
    offset: {x: number; y: number};
    /**
     * Whether the drag is currently active
     */
    isDragging: boolean;
    /**
     * The function to start the drag
     */
    dragStart: (event: React.MouseEvent) => void;
    /**
     * The function to reset the drag
     */
    resetDrag: () => void;
};

/**
 * A hook to handle simple drag operations
 * @returns An object with the offset, whether the drag is currently active,
 * and the functions to start and reset the drag
 */
export const useSimpleDrag = (): ReturnValue => {
    const [offset, setOffset] = useState({x: 0, y: 0});
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({x: 0, y: 0});
    const [initialOffset, setInitialOffset] = useState({x: 0, y: 0});

    const handleInitDrag = (event: React.MouseEvent) => {
        setIsDragging(true);
        setDragStart({x: event.clientX, y: event.clientY});
        setInitialOffset(offset);
        event.preventDefault();
    };

    const handleMouseMove = useCallback(
        (event: MouseEvent) => {
            if (!isDragging) return;

            const deltaX = event.clientX - dragStart.x;
            const deltaY = event.clientY - dragStart.y;

            setOffset({
                x: initialOffset.x + deltaX,
                y: initialOffset.y + deltaY,
            });
        },
        [isDragging, dragStart, initialOffset]
    );

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, [setIsDragging]);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        }

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, dragStart, initialOffset, handleMouseMove, handleMouseUp]);

    const returnValue = {
        offset,
        isDragging,
        dragStart: handleInitDrag,
        resetDrag: () => setOffset({x: 0, y: 0}),
    };

    return returnValue;
};
